import { QuoteType, QuoteWarrantyTerm } from '@/lib/types'

export const PROPOSAL_LAYOUT_V2_CUTOFF_DATE = new Date('2026-07-17T07:00:00.000Z')
export const PROPOSAL_AGREEMENT_SECTION_ID = 'proposal-agreement'
export const SERVICE_AGREEMENT_ID = 'agreement-service-v1'
export const BUDGET_AGREEMENT_ID = 'agreement-budget-v1'

export enum ProposalLayoutVersion {
  Legacy = 1,
  AcceptanceAndMilestones = 2,
}

export type AgreementDescriptor = {
  id: string
  title: 'Agreement' | 'Master Services Agreement' | 'Service Agreement' | 'Terms and Conditions'
  version: number
}

type VersionedProposal = {
  dsy_approvalsignaturedatetime?: string | null
  proposal_layout_version?: ProposalLayoutVersion
}

export function getProposalLayoutVersion(
  quote: VersionedProposal | null | undefined,
  now = new Date()
): ProposalLayoutVersion {
  if (quote?.proposal_layout_version) return quote.proposal_layout_version

  const versionDate = quote?.dsy_approvalsignaturedatetime
    ? new Date(quote.dsy_approvalsignaturedatetime)
    : now

  return versionDate >= PROPOSAL_LAYOUT_V2_CUTOFF_DATE
    ? ProposalLayoutVersion.AcceptanceAndMilestones
    : ProposalLayoutVersion.Legacy
}

export function getAgreementDescriptor({
  quoteType,
  warrantyTerm,
  agreementVersion,
}: {
  quoteType: QuoteType | number | null | undefined
  warrantyTerm: QuoteWarrantyTerm | number | null | undefined
  agreementVersion: number
}): AgreementDescriptor {
  if (quoteType === QuoteType.Budget) {
    return { id: BUDGET_AGREEMENT_ID, title: 'Agreement', version: 1 }
  }

  if (agreementVersion === 5 && quoteType === QuoteType.ServiceInvoice) {
    // From the v5 cutoff service work has its own agreement, with a fixed warranty
    return { id: SERVICE_AGREEMENT_ID, title: 'Service Agreement', version: 1 }
  }

  if (agreementVersion === 5) {
    // One v5 text; the term in the id records which Coverage Term was accepted
    const term = warrantyTerm === QuoteWarrantyTerm.OneYear ? 'one-year' : 'three-year'
    return { id: `agreement-msa-v5-${term}`, title: 'Master Services Agreement', version: 5 }
  }

  if (agreementVersion >= 3) {
    const version = warrantyTerm === QuoteWarrantyTerm.OneYear ? 4 : 3
    return { id: `agreement-msa-v${version}`, title: 'Master Services Agreement', version }
  }

  return {
    id: `agreement-terms-v${agreementVersion}`,
    title: 'Terms and Conditions',
    version: agreementVersion,
  }
}

export function getAcceptanceStatement(title: AgreementDescriptor['title']) {
  return `I have reviewed and agree to this proposal and the ${title} below.`
}
